import { Formik, FormikProps } from 'formik';
import { sumBy } from 'lodash';
import React, { useState } from 'react';
import * as yup from 'yup';
import { applyBulkActionToMedicationList } from '../../Async/MedicationListsAsync';
import { useError } from '../../Components/ErrorBoundary';
import { FullScreenModal } from '../../Components/FullScreenModal';
import { StyledNavigationPrompt } from '../../Components/Prompts/StyledNavigationPrompt';
import { TimeUnits } from '../../Config/MedicationConfig';
import { BulkActionRequest, Slicer } from '../../Contracts/BulkActionRequest.Request';
import { ListBulkAction, ListSearchAction } from '../../Contracts/MedicationListModels';
import { requiredField } from '../../Medications.en';
import { isDomainValidationError, ValidationFailure } from '../../Shared/DomainValidationFunctions';
import { Reference } from '../../Shared/SharedModels';
import { Maybe } from '../../Shared/Types';
import {
    DefaultDuration,
    MedicationListDefaultOverride,
    MedicationListDefaultOverrideStepState
} from '../MedicationListSharedSteps/MedicationListDefaultOverride';
import { MedicationListErrors } from '../MedicationListSharedSteps/MedicationListErrors';
import { MedicationListSliceStep, SliceStepState } from '../MedicationListSharedSteps/MedicationListSliceStep';
import { MedicationListBulkActionButtons } from './MedicationListBulkActionButtons';
import { MedicationListBulkActionPlaceholder } from './MedicationListBulkActionPlaceholder';
import styles from './MedicationListDialogBulkActionWizard.module.scss';

export enum UpdateMedicationListStep {
    ListSlices,
    DefaultOverride,
    UpdatingMedicationList,
    Error
}

function getTitle(bulkAction: ListBulkAction, step: UpdateMedicationListStep) {
    switch (step) {
        case UpdateMedicationListStep.ListSlices:
            return bulkAction === ListBulkAction.Append
                ? 'Add medication to your list'
                : bulkAction === ListBulkAction.Remove
                ? 'Remove medication from your list'
                : // bulkAction === ListBulkAction.Sync
                  'Synchronise medication in your list';
        case UpdateMedicationListStep.DefaultOverride:
            return 'Define your default dosage instructions for your list';
        case UpdateMedicationListStep.Error:
            return bulkAction === ListBulkAction.Append
                ? 'Unable to add to medication list.'
                : bulkAction === ListBulkAction.Remove
                ? 'Unable to remove from medication list.'
                : // bulkAction === ListBulkAction.Sync
                  'Unable to synchronise medication list.';
        case UpdateMedicationListStep.UpdatingMedicationList:
            return '';
    }
}

function getFormInfo(bulkAction: ListBulkAction, step: UpdateMedicationListStep) {
    switch (step) {
        case UpdateMedicationListStep.ListSlices:
            return bulkAction === ListBulkAction.Append
                ? 'Add groups of medications that will share common prescribing characteristics. Select at least one data set to continue.'
                : bulkAction === ListBulkAction.Remove
                ? 'Remove groups of medications that will share common prescribing characteristics. Select at least one data set to continue.'
                : // bulkAction === ListBulkAction.Sync &&
                  'Select the groups of medications that you want to update in your list. We will review, add or remove any medication items that have changed.';
        case UpdateMedicationListStep.DefaultOverride:
            return 'The duration and dosage are used by default for any drug from the collection. You can customise individual drugs later. Use to help manage drug review cycles and streamline prescribing.';
        case UpdateMedicationListStep.Error:
            return bulkAction === ListBulkAction.Append
                ? 'Unable to add to medication list.'
                : bulkAction === ListBulkAction.Remove
                ? 'Unable to remove from medication list.'
                : // bulkAction === ListBulkAction.Sync
                  'Unable to synchronise medication list.';
        case UpdateMedicationListStep.UpdatingMedicationList:
            return '';
    }
}

function getProgress(bulkAction: ListBulkAction, step: UpdateMedicationListStep): Maybe<number> {
    if (bulkAction === ListBulkAction.Sync) return;
    if (bulkAction === ListBulkAction.Remove) return;

    switch (step) {
        case UpdateMedicationListStep.ListSlices:
            return 0;
        case UpdateMedicationListStep.DefaultOverride:
            return 50;
        case UpdateMedicationListStep.UpdatingMedicationList:
            return; // Return undefined to hide progress bar.
    }
}

export type UpdateMedicationListForm = SliceStepState & MedicationListDefaultOverrideStepState;

const createMedicationListSchema = yup.object<UpdateMedicationListForm>({
    bulkAction: yup.mixed<ListBulkAction>(),
    atcCodes: yup.array<any>().test({
        name: 'Characteristic required',
        message: 'At least one item required',
        test(this: yup.TestContext, value: Reference[]) {
            const form = this.parent as UpdateMedicationListForm;

            if (value.length > 0) return true;

            return form.schedules.length > 0 || form.medicinalProducts.length > 0;
        }
    }),
    schedules: yup.array<any>().test({
        name: 'Characteristic required',
        message: 'At least one item required',
        test(this: yup.TestContext, value: Reference[]) {
            const form = this.parent as UpdateMedicationListForm;

            if (value.length > 0) return true;

            return form.atcCodes.length > 0 || form.medicinalProducts.length > 0;
        }
    }),
    medicinalProducts: yup.array<any>().test({
        name: 'Characteristic required',
        message: 'At least one item required',
        test(this: yup.TestContext, value: Reference[]) {
            const form = this.parent as UpdateMedicationListForm;

            if (value.length > 0) return true;

            return form.schedules.length > 0 || form.atcCodes.length > 0;
        }
    }),
    defaultDosageInstructions: yup.string().test({
        name: 'Default dosage instructions must exist if overridden is active',
        message: requiredField,
        test(this: yup.TestContext, value: string) {
            const form = this.parent as UpdateMedicationListForm;
            if (form.defaultDosageInstructionsOverridden) {
                if (!value) return false;
            }

            return true;
        }
    }),
    defaultDosageInstructionsOverridden: yup.boolean(),
    defaultDuration: yup.object<any>().test({
        name: 'Default duration must have a number if overridden is active',
        message: requiredField,
        test(this: yup.TestContext, value: DefaultDuration) {
            const form = this.parent as UpdateMedicationListForm;
            if (form.defaultDurationOverridden) {
                if (!value.number) return false;
            }

            return true;
        }
    }),
    defaultDurationOverridden: yup.boolean(),
    defaultPackAmount: yup.string().test({
        name: 'Default pack amount must have a number if overridden is active',
        message: requiredField,
        test(this: yup.TestContext, value: string) {
            const form = this.parent as UpdateMedicationListForm;
            if (form.defaultPackAmountOverridden) {
                if (!value) return false;
            }

            return true;
        }
    }),
    defaultPackAmountOverridden: yup.boolean()
});

const MIN_CREATE_DURATION = 2000;

export const MedicationListDialogBulkActionWizard = (props: {
    medListId: string;
    bulkAction: ListBulkAction;
    onClose(): void;
    onComplete(result: { itemsAdded: number; itemsRemoved: number }): void;
}) => {
    const [step, setStep] = useState(UpdateMedicationListStep.ListSlices);
    const [errors, setErrors] = useState<ValidationFailure[]>([]);
    const throwError = useError();

    const handleError = (errors: ValidationFailure[]) => {
        setStep(UpdateMedicationListStep.Error);
        setErrors(errors);
    };

    const title = getTitle(props.bulkAction, step);
    const info = getFormInfo(props.bulkAction, step);
    const progress = getProgress(props.bulkAction, step);

    const handleClose = () => {
        props.onClose();
    };

    const handleBack = () => {
        if (step === UpdateMedicationListStep.DefaultOverride) setStep(UpdateMedicationListStep.ListSlices);
    };

    const hideBackButton = step === UpdateMedicationListStep.ListSlices || step === UpdateMedicationListStep.Error;

    return (
        <FullScreenModal
            title={title}
            info={info}
            progressPercent={progress}
            onBack={handleBack}
            hideBackButton={hideBackButton}
            nonInteractivePlaceholder={
                step === UpdateMedicationListStep.UpdatingMedicationList ? (
                    <MedicationListBulkActionPlaceholder />
                ) : undefined
            }
            onClose={handleClose}
        >
            <div className={styles.container}>
                <Formik<UpdateMedicationListForm>
                    initialValues={{
                        bulkAction: props.bulkAction,
                        atcCodes: [],
                        schedules: [],
                        medicinalProducts: [],
                        defaultDuration: {
                            timeUnits: TimeUnits.Days,
                            number: ''
                        },
                        defaultDurationOverridden: false,
                        defaultDosageInstructions: '',
                        defaultDosageInstructionsOverridden: false,
                        defaultPackAmount: '',
                        defaultPackAmountOverridden: false
                    }}
                    validationSchema={createMedicationListSchema}
                    onSubmit={async (values, formikActions) => {
                        setStep(UpdateMedicationListStep.UpdatingMedicationList);
                        const timeout = new Promise(resolve => setTimeout(resolve, MIN_CREATE_DURATION));

                        const defaults = {
                            defaultDosageInstructions: values.defaultDosageInstructionsOverridden
                                ? values.defaultDosageInstructions
                                : '',
                            defaultDuration: values.defaultDurationOverridden
                                ? {
                                      timeUnits: values.defaultDuration.timeUnits,
                                      number: Number(values.defaultDuration.number)
                                  }
                                : null,
                            defaultPackAmount: values.defaultPackAmountOverridden
                                ? !!values.defaultPackAmount && !isNaN(Number(values.defaultPackAmount))
                                    ? Number(values.defaultPackAmount)
                                    : null
                                : null
                        };

                        const selectionSlicers: Slicer[] = [
                            ...values.schedules.map(
                                (it): Slicer => ({
                                    searchCode: it.code,
                                    searchAction: ListSearchAction.PoisonSchedule,
                                    ...defaults
                                })
                            ),
                            ...values.atcCodes.map(
                                (it): Slicer => ({
                                    searchCode: it.code,
                                    searchAction: ListSearchAction.AtcCode,
                                    ...defaults
                                })
                            ),
                            ...values.medicinalProducts.map(
                                (it): Slicer => ({
                                    searchCode: it.code,
                                    searchAction: ListSearchAction.MedicinalProduct,
                                    ...defaults
                                })
                            )
                        ];

                        const bulkActionPayload: BulkActionRequest = {
                            bulkAction: props.bulkAction,
                            selectionSlicers
                        };

                        try {
                            const [results] = await Promise.all([
                                applyBulkActionToMedicationList(props.medListId, bulkActionPayload),
                                timeout
                            ]);

                            const itemsAdded = sumBy(
                                results.results.filter(it => it.bulkAction === ListBulkAction.Append),
                                it => it.recordsChanged
                            );

                            const itemsRemoved = sumBy(
                                results.results.filter(it => it.bulkAction === ListBulkAction.Remove),
                                it => it.recordsChanged
                            );

                            props.onComplete({
                                itemsAdded,
                                itemsRemoved
                            });
                        } catch (err) {
                            // Error handling pattern:
                            // Capture all errors that we want to handle locally.
                            if (isDomainValidationError(err)) {
                                let errors: ValidationFailure[] = [];

                                if (err.content) {
                                    errors = [...err.content];
                                } else {
                                    errors = [
                                        {
                                            errorMessage: (err.message as string) ?? 'Unknown error occurred',
                                            propertyName: ''
                                        }
                                    ];
                                }

                                formikActions.resetForm({ values });
                                handleError(errors);
                            } else {
                                // Allow the rest to flow through to the 'throwError' so ErrorBoundary handles them.
                                throwError(err);
                            }
                        }
                    }}
                >
                    {(formProps: FormikProps<UpdateMedicationListForm>) => {
                        const handleContinue = async () => {
                            const errors = await formProps.validateForm();

                            if (step === UpdateMedicationListStep.ListSlices) {
                                const noErrors =
                                    Object.keys(errors).filter(it =>
                                        ['atcCodes', 'schedules', 'medicinalProducts'].includes(it)
                                    ).length === 0;
                                if (noErrors) {
                                    // If we're doing a remove action, there's no value in the default override step.
                                    if (
                                        props.bulkAction === ListBulkAction.Remove ||
                                        props.bulkAction === ListBulkAction.Sync
                                    ) {
                                        formProps.submitForm();
                                    } else {
                                        setStep(UpdateMedicationListStep.DefaultOverride);
                                    }
                                } else
                                    formProps.setTouched({
                                        atcCodes: [{ code: true }],
                                        schedules: [{ code: true }],
                                        medicinalProducts: [{ code: true }]
                                    });
                            } else if (step === UpdateMedicationListStep.DefaultOverride) {
                                formProps.submitForm();
                            }
                        };

                        return (
                            <>
                                {step === UpdateMedicationListStep.ListSlices && (
                                    <MedicationListSliceStep onContinue={handleContinue} />
                                )}
                                {step === UpdateMedicationListStep.DefaultOverride && <MedicationListDefaultOverride />}
                                {step === UpdateMedicationListStep.Error && <MedicationListErrors errors={errors} />}
                                <MedicationListBulkActionButtons
                                    step={step}
                                    formProps={formProps}
                                    onClose={handleClose}
                                    onContinue={handleContinue}
                                />
                                <StyledNavigationPrompt when={formProps.isSubmitting || formProps.dirty} />
                            </>
                        );
                    }}
                </Formik>
            </div>
        </FullScreenModal>
    );
};
